import React from 'react';
import { Helmet } from 'react-helmet-async';
import { REVIEWS, OVERALL_RATING_TEXT } from '../constants/reviews';

// Default meta description — the rating is the verified Google figure from
// src/constants/reviews.js (no "number-one" or other unverifiable superlatives).
const DEFAULT_DESCRIPTION = `Book the ${OVERALL_RATING_TEXT}★-rated on Google NYC horse carriage ride in Central Park (${REVIEWS.google.count} reviews). Romantic tours for proposals, anniversaries & sightseeing. Since 1999.`;

const SEO = ({
  title = "NYC Horse Carriage Rides in Central Park | NYC Royal Carriage",
  description = DEFAULT_DESCRIPTION,
  canonical = "",
  ogImage = "/images/hero-bg-og.jpg",
  ogType = "website",
  noindex = false,
  breadcrumbs = null,
  schemaData = null
}) => {
  const siteUrl = "https://nycroyalcarriage.com";
  // Cloudflare Pages serves directory routes with trailing slash (308 redirect).
  // Canonical must match the final URL to avoid redirect chains for crawlers.
  const fullCanonical = canonical
    ? `${siteUrl}${canonical === '/' ? '/' : (canonical.endsWith('/') ? canonical : canonical + '/')}`
    : `${siteUrl}/`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  // NOTE: LocalBusiness, WebSite, and other global schemas are in public/index.html.
  // WebPage schema is generated HERE (dynamic per page) — removed from index.html.

  // WebPage schema — dynamic per page (url + name match each page's canonical)
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${fullCanonical}#webpage`,
    "name": title,
    "url": fullCanonical,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "[data-speakable]", ".hero-description"]
    }
  };

  // SiteNavigationElement — helps search engines understand site hierarchy
  const siteNavSchema = canonical === "/" ? {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Main Navigation",
    "hasPart": [
      { "@type": "WebPage", "name": "Tours & Prices", "url": `${siteUrl}/tours/` },
      { "@type": "WebPage", "name": "About Us", "url": `${siteUrl}/about/` },
      { "@type": "WebPage", "name": "Proposals", "url": `${siteUrl}/proposals/` },
      { "@type": "WebPage", "name": "Gallery", "url": `${siteUrl}/gallery/` },
      { "@type": "WebPage", "name": "Events", "url": `${siteUrl}/events/` },
      { "@type": "WebPage", "name": "Blog", "url": `${siteUrl}/blog/` },
      { "@type": "WebPage", "name": "FAQ", "url": `${siteUrl}/faq/` },
      { "@type": "WebPage", "name": "Reviews", "url": `${siteUrl}/reviews/` },
      { "@type": "WebPage", "name": "Contact", "url": `${siteUrl}/contact/` }
    ]
  } : null;

  // BreadcrumbList schema - auto-generated from breadcrumbs prop or canonical path
  const breadcrumbSchema = (() => {
    if (breadcrumbs) {
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": crumb.name,
          "item": crumb.url ? `${siteUrl}${crumb.url.endsWith('/') ? crumb.url : crumb.url + '/'}` : undefined
        }))
      };
    }
    // Homepage: skip BreadcrumbList (Google requires 2+ items)
    if (canonical === "/") {
      return null;
    }
    // Auto-generate from canonical path
    if (canonical && canonical !== "/") {
      const pathNames = {
        "/tours": "Tours & Prices",
        "/about": "About Us",
        "/contact": "Contact",
        "/faq": "FAQ",
        "/gallery": "Photo Gallery",
        "/blog": "Blog",
        "/events": "Events",
        "/proposals": "Proposals",
        "/reviews": "Reviews",
        "/horses": "Our Horses",
        "/tour-guide": "Tour Guide",
        "/book": "Book Your Ride",
        "/privacy-policy": "Privacy Policy",
        "/terms-of-service": "Terms of Service"
      };
      const pageName = pathNames[canonical] || canonical.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
          { "@type": "ListItem", "position": 2, "name": pageName, "item": fullCanonical }
        ]
      };
    }
    return null;
  })();

  return (
    <>
    <Helmet defer={false}>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="author" content="NYC Royal Carriage" />
      {/* viewport is defined in public/index.html — no duplicate here */}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:type" content={fullOgImage.endsWith('.webp') ? 'image/webp' : 'image/jpeg'} />
      <meta property="og:image:width" content="1920" />
      <meta property="og:image:height" content="1080" />
      <meta property="og:site_name" content="NYC Royal Carriage" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@nychorsecarriage" />
      <meta name="twitter:creator" content="@nychorsecarriage" />

      {/* Additional Meta Tags */}
      {/* theme-color is defined in public/index.html (#FAF7F2 — matches warm-white body bg).
          Removed from here to avoid duplicate-meta and browser-chrome inconsistency. */}
      <meta name="msapplication-TileColor" content="#1f2937" />
      {/* apple-mobile-web-app-capable is in public/index.html — no duplicate here */}
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="NYC Horse Carriage" />

      {/* Hreflang removed: client-side translation via query params (?lang=xx) does not
         produce separate server-rendered pages, so hreflang tags were misleading crawlers.
         Re-add only when proper server-rendered translated pages are implemented. */}

      {/* Preconnect hints are in public/index.html — no duplicates here */}

    </Helmet>
    {/* JSON-LD schemas — rendered outside Helmet to prevent react-snap
        duplication (Helmet bakes scripts into pre-rendered HTML AND
        re-injects on hydrate, creating 2 copies).
        dangerouslySetInnerHTML is safe here: all content is hardcoded
        static schema data, never user input.
        suppressHydrationWarning prevents errors when schema content
        differs between pre-render and client hydrate. */}
    <div suppressHydrationWarning dangerouslySetInnerHTML={{ __html:
      `<script type="application/ld+json">${JSON.stringify(webPageSchema)}</script>` +
      (breadcrumbSchema ? `<script type="application/ld+json">${JSON.stringify(breadcrumbSchema)}</script>` : '') +
      (siteNavSchema ? `<script type="application/ld+json">${JSON.stringify(siteNavSchema)}</script>` : '') +
      (schemaData ? `<script type="application/ld+json">${JSON.stringify(schemaData)}</script>` : '')
    }} />
    </>
  );
};

export default SEO;
